import { mdiCheck, mdiClose, mdiEmailOutline, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { Row } from 'react-table';
import { MessagingContext } from '../../services';
import { SidePanel } from '../../ui';
import { ActiveFilterItem, DataTablePresenter, FilterMethod, ICustomFilter, ITableColumn, ITableFetchOptions } from '../data-table';
import * as S from './message-list.styles';
import { getNickname, IMessagingAuthor, IMessagingMessage, isLastAuthor } from './util';

interface IMessageListProps {
    inboxCount: number;
    inboxMessages: IMessagingMessage[];
    doneCount?: number;
    doneMessages?: IMessagingMessage[];
    onFetchData: (newTableState: ITableFetchOptions) => Promise<void>;
    onShowCompose: () => void;
    onFilterRemove?: () => void;
    selectedMessageId: string | undefined;
    filter?: ICustomFilter;
    showInboxDoneTabs?: boolean; // Only show tabs when on claim page
}

export const MessageListPanel: React.FunctionComponent<IMessageListProps> = observer((
    props: IMessageListProps,
): React.ReactElement => {
    const { filter, inboxMessages, doneMessages = [], inboxCount, doneCount, onFetchData, selectedMessageId, onShowCompose, onFilterRemove, showInboxDoneTabs = false } = props;
    // Inbox is preselected by default (showDoneMessages = false)
    const [showDoneMessages, setShowDoneMessages] = useState(false);

    const context = useContext(MessagingContext);
    const { isReadOnly } = context;

    const pageSize = 100;

    const formatDate = (d: Date): string => format(d, 'MMM d, yyyy');
    const parseDate = (d: string | undefined): Date => (d ? new Date(d) : new Date());

    const renderFromField = (message: IMessagingMessage, author: IMessagingAuthor): React.ReactElement => {
        return (
            <>
                <S.IndicatorField unread={!message.read && isLastAuthor(message, author)} author={author} />
                <S.FromField unread={!message.read && isLastAuthor(message, author)}>{getNickname(author)}</S.FromField>
            </>
        );
    };

    const columns: ITableColumn[] = [
        {
            id: 'From',
            Header: 'From',
            Cell: ({ row }): React.ReactElement => {
                const { author, comments } = row.original;
                return (
                    <>
                        {renderFromField(row.original, author)}
                        {comments > 1 && <S.CommentCount>{comments}</S.CommentCount>}
                    </>
                );
            },
            maxWidth: 200,
        },
        {
            id: 'Subject',
            Header: 'Subject',
            Cell: ({ row }): React.ReactElement => {
                const { documents, subject, read } = row.original;
                return (
                    <S.SubjectField unread={!read}>
                        {subject}
                        {documents > 0 && <S.AttachmentField count={documents} />}
                    </S.SubjectField>
                );
            },
        },
        {
            id: 'Date',
            Header: 'Date',
            accessor: 'updated',
            Cell: ({ cell }): string => formatDate(parseDate(cell.value)),
            maxWidth: 200,
        },
    ];

    const onRowClick = () => {
        const { onMessageClose, onMessageOpen } = context;
        const selectedId = selectedMessageId;
        return async (row: Row<IMessagingMessage>): Promise<void> => {
            const {
                original: { id },
            } = row;
            if (id === selectedId) {
                await (onMessageClose && onMessageClose());
            } else {
                await (onMessageOpen && id && onMessageOpen(id));
            }
        };
    };

    const onCloseClick = (): void => {
        context.onMessageClose && context.onMessageClose();
    };

    // Initialize presenter once (similar to constructor in class component)
    const presenterRef = useRef<DataTablePresenter<IMessagingMessage> | undefined>(undefined);
    if (!presenterRef.current) {
        presenterRef.current = new DataTablePresenter({
            name: 'message-list-panel',
            columns,
            data: [],
            filterSchemas: [],
        });
        // Set page size to 100 to match the onFetchData pageSize and prevent initial fetch with default pageSize 10
        presenterRef.current.onPageSizeUpdate(100);
    }
    const presenter = presenterRef.current!;

    // Only use done messages if tabs are enabled (on claim page)
    const visibleMessages =
        showInboxDoneTabs && showDoneMessages ? doneMessages : inboxMessages;

    // Use provided count for inbox; for done fall back to array length to avoid stale totals when count isn't passed
    const visibleCount =
        showInboxDoneTabs && showDoneMessages ? doneCount ?? doneMessages.length : inboxCount;

    // Update presenter data when source data or tab state changes
    // Depend on source arrays and counts to update when data actually changes
    useLayoutEffect(() => {
        presenter.onDataUpdate(visibleMessages);
        presenter.onDataTotalChange(visibleCount);
    }, [presenter, doneMessages, inboxMessages, doneCount, inboxCount, showDoneMessages, showInboxDoneTabs]);

    return (
        <SidePanel>
            <S.MessageListPanel>
                <S.Header>
                    <S.ComposeButton disabled={isReadOnly} variant="primary" onClick={onShowCompose}>
                        <S.ComposeIcon path={mdiPlus} size={0.75} />
                        <S.ComposeLabel>Start new message</S.ComposeLabel>
                    </S.ComposeButton>
                    <S.CloseButton variant="fab-light" onClick={onCloseClick}>
                        <S.CloseIcon path={mdiClose} size={1} />
                    </S.CloseButton>
                </S.Header>
                {!!filter && (
                    <div>
                        <S.FilterButton
                            variant="text"
                            onClick={onFilterRemove ? onFilterRemove : undefined}
                            disabled={!!filter.locked}>
                            <ActiveFilterItem filter={filter} />
                            {!filter.locked && <Icon path={mdiClose} size={0.75} />}
                        </S.FilterButton>
                    </div>
                )}
                {showInboxDoneTabs && (
                    <>
                        <S.TabsDivider />
                        <S.TabsControlWrapper>
                            <S.TabsContainer>
                                <S.TabButton
                                    variant="outlined"
                                    disabled={!showDoneMessages}
                                    onClick={() => setShowDoneMessages(false)}>
                                    <Icon path={mdiEmailOutline} size={0.75} />
                                    <span>Inbox</span>
                                    <S.ControlCount>{inboxCount}</S.ControlCount>
                                </S.TabButton>
                                <S.TabButton
                                    variant="outlined"
                                    disabled={showDoneMessages}
                                    onClick={async () => {
                                        if (!showDoneMessages) {
                                            setShowDoneMessages(true);
                                            // Always fetch when switching to Done tab to ensure correct filters are applied
                                            if (onFetchData) {
                                                const tableDefaults = presenter.getTableDefaults();
                                                await onFetchData({
                                                    pageIndex: tableDefaults?.pageIndex || 0,
                                                    // Keep pageSize consistent with presenter initialization (100)
                                                    pageSize: tableDefaults?.pageSize || pageSize,
                                                    sortBy: tableDefaults?.sortBy || [],
                                                    validFilters: [
                                                        ...(presenter.validFilters || []),
                                                        {
                                                            filterId: 'filterOpen',
                                                            filterMethod: FilterMethod.EQ,
                                                            filterType: 'String',
                                                            value: '0',
                                                        },
                                                    ],
                                                });
                                            }
                                        }
                                    }}>
                                    <Icon path={mdiCheck} size={0.75} />
                                    <span>Done</span>
                                </S.TabButton>
                            </S.TabsContainer>
                        </S.TabsControlWrapper>
                    </>
                )}
                {/* {filter?.filterId} */}
                <S.TableContainer>
                    <S.MessageTable
                        presenter={presenter}
                        isLoading={context.isListLoading}
                        onFetch={onFetchData}
                        onRowClick={onRowClick()}
                        hideFiltering={true}
                        hideNavigation={true}
                        hidePagination={true}
                        disableSorting={true}
                        hideSettings={true}
                    />
                </S.TableContainer>
            </S.MessageListPanel>
        </SidePanel>
    );
});
